import FalseIcon from '@mui/icons-material/Clear';
import TrueIcon from '@mui/icons-material/Done';
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef } from 'react';
import {
  ArrayField,
  BooleanField,
  ChipField,
  Datagrid,
  DateField,
  EditButton,
  EmailField,
  Filter,
  FunctionField,
  Labeled,
  List,
  NumberField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  SelectArrayInput,
  SelectField,
  Show,
  SimpleShowLayout,
  SingleFieldList,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useListContext,
  usePermissions,
  useRecordContext,
} from "react-admin";
import { FillinsDatagrids, Permission, PrintButton, ReportPrint, Tooltip } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import { CNPJField, PhoneField } from '../../components/Fields';
import ListPagination from '../../components/ListPagination';
import { GestaoClickStation } from './GestaoClickStation';
import { CommissioningType, commissioningTypeChoices } from '../../models';
import { PERMISSIONS, daysOfTheWeek, minimumValueCashbackToolTip } from '../../constants';
import useExpandedEvery from '../../hooks/useExpandedEvery';
import { FinancePaymentMethodType, IntegrationType, TagType } from '../../models';
import { formatNumber } from '../../utils';

const QrcodePanel = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <Datagrid bulkActionButtons={false} data={record.hoses} total={1}>
      <TextField source="hoseNumber" label="Bico" textAlign="center" />
      <TextField source="fuel.name" label="Combustível" textAlign="center" />
    </Datagrid>
  );
};

const ListQrcodes = () => {
  const record = useRecordContext();
  const qrCodesRef = useRef();
  if (!record) { return null; }

  const ActionsQrCodes = () => {
    const { data } = useListContext();
    const [expandedEvery, toggleExpandedEvery] = useExpandedEvery("station-qrcodes", (data || []).map((code) => code.id));

    useEffect(() => {
      if (!expandedEvery) {
        toggleExpandedEvery();
      }
    }, [data]);


    const onClick = () => {
      if (!expandedEvery) {
        toggleExpandedEvery();
      }
    }

    return (
      <TopToolbar>
        <PrintButton
          onClick={onClick}
          printRef={qrCodesRef}
        />
      </TopToolbar>
    )
  }

  const DataGridStationQrCode = () => {
    const props = useListContext();
    const data = (props.data || []).filter((station) => station.hoses.some((hose) => hose.hoseNumber));

    return (
      <Datagrid bulkActionButtons={false} expand={<QrcodePanel />} data={data} total={1}>
        <TextField source="qrcode" label="Qrcode" sortable={false} />
      </Datagrid>
    );
  }

  return (
    <List
      title=" "
      resource="station-qrcodes"
      filter={{ numericIds: record.numericIds }}
      pagination={false}
      exporter={false}
      actions={<ActionsQrCodes />}
      syncWithLocation={false}
    >
      <ReportPrint ref={qrCodesRef} title="Mapa de Pista">
        <DataGridStationQrCode />
      </ReportPrint>
    </List>
  )
};

const FilterTablePrint = ({ stationName }) => {
  const { filterValues } = useListContext();

  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, stationName }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} />
      <DateField source="to" textAlign="center" label="Até" sortable={false} />
      <ReferenceArrayField label="Combustíveis" source="fuelIds" textAlign="center" basePath="fuels" reference="fuels">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <TextField source="stationName" label="Posto" />
    </Datagrid>
  );
};

const FilterFillin = (props) => {
  // let propsCopy = Object.assign({}, props);
  // delete propsCopy.record.fuelIds;
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <Filter>
      <BetweenDatesInput alwaysOn />
      <ReferenceArrayInput
        source="fuelIds"
        reference="fuels"
        sort={{ field: "name", order: "ASC" }}
        perPage={null}
        alwaysOn
      >
        <SelectArrayInput
          style={{ minWidth: 180 }}
          label="Combustíveis"
          optionText={"name"}
        />
      </ReferenceArrayInput>
    </Filter>
  );
};

const ListFillins = ({ ...props }) => {
  const station = useRecordContext();
  const fillinsRef = useRef();
  if (!station) { return null; }

  const ListFillinsActions = () => (
    <TopToolbar>
      <PrintButton
        printRef={fillinsRef}
      />
    </TopToolbar>
  );

  return (
    <List
      title=" "
      resource="fillins"
      sort={{ field: "createdAt", order: "DESC" }}
      filterDefaultValues={{
        from: moment().startOf("day").subtract(1, "month").toISOString(),
        to: moment().endOf("day").toISOString()
      }}
      filter={{ stationIds: [station.id], showSummary: true, skipJoins: true }}
      perPage={25}
      pagination={<ListPagination />}
      exporter={false}
      actions={<ListFillinsActions />}
      filters={<FilterFillin {...props} />}
      disableSyncWithLocation
    >
      <ReportPrint ref={fillinsRef} title="Abastecimentos" landscape>
        <Box sx={{ display: 'none', '@media print': { display: 'block' }, mb: 1 }}>
          <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>Filtros</Typography>
          <FilterTablePrint stationName={station.name} />
        </Box>
        <FillinsDatagrids />
      </ReportPrint>
    </List>
  );
};

const StationIdentify = () => {
  const NumericIdField = () => {
    const record = useRecordContext();
    if (!record) { return null; }

    return record.numericIds?.map((_, ind) => (
      <ChipField source={`numericIds[${ind}]`} />
    ));
  };

  const RtiTokenField = () => {
    const record = useRecordContext();
    if (!record) { return null; }
    return (
      <TextField
        label="Token RTI"
        source="rtiToken"
      />
    );
  }

  return (
    <SimpleShowLayout sx={{ padding: 0 }}>
      <TextField label="ID" source="id" />
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <ArrayField source="numericIds" label="IDs Numéricos">
              <NumericIdField />
            </ArrayField>
          )
        })
      }
      <TextField source="name" label="Nome" />
      <EmailField source="email" label="Email" emptyText='-' />
      <CNPJField source="cnpj" label="CNPJ" />
      <PhoneField source="phone" label="Telefone" />
      {Permission({
        permission: PERMISSIONS.ADMIN,
        children: (
          <ReferenceField
            source="chainId"
            reference="chains"
            sort={{ field: 'name', order: 'ASC' }}
            label="Rede"
            link={false}
          >
            <TextField
              source="name"
            />
          </ReferenceField>
        )
      })}
      <ReferenceArrayField
        sort={{ field: 'name', order: 'ASC' }}
        source="fuelIds"
        reference="fuels"
        label="Combustível"
      >
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField
        style={{ minWidth: 260 }}
        sort={{ field: 'name', order: 'ASC' }}
        source="serviceIds"
        reference="services"
      >
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>

      <ReferenceArrayField
        style={{ minWidth: 260 }}
        sort={{ field: 'name', order: 'ASC' }}
        filter={{ type: TagType.station }}
        source="tagIds"
        label="Etiquetas"
        reference="chains/chainId/tag"
      >
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <SelectField
              label="Integração"
              emptyText="--"
              source="integration"
              choices={IntegrationType.integrationChoices}
            />
          )
        })
      }
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <RtiTokenField />
          )
        })
      }
    </SimpleShowLayout>
  )
}

const SectionTitle = ({ children }) => (
  <>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, mb: 0.5 }}>{children}</Typography>
    <Divider sx={{ mb: 1.5, maxWidth: 600 }} />
  </>
);

const StationConfig = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <SimpleShowLayout sx={{ padding: 0 }}>
      <SectionTitle>Abastecimento</SectionTitle>
      {Permission({ permission: PERMISSIONS.ADMIN, children: <BooleanField label="Preset por Valor (Encher tanque)" source="presetUsingValue" /> })}
      <BooleanField label="Usar integração com Software House" source="useIntegrationSoftwareHouse" />
      <BooleanField label="Acompanhar Abastecimento GNV em tempo real" source="useFillinFollowUpGnv" />
      <BooleanField label="Acompanhar Abastecimento em tempo real (Outros combustíveis)" source="useFillinFollowUp" />
      <BooleanField label="Mostrar preço durante o abastecimento" source="appShowPricePerLiterOnFillin" />

      <SectionTitle>Níveis de Preço</SectionTitle>
      <BooleanField label="Usar níveis de preço" source="usePriceTiers" />
      <FunctionField render={r => r.priceTierLabels?.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Rótulos dos níveis de preço</Typography>
          <Table size="small" sx={{ maxWidth: 500, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700, width: 80 }}>Índice</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rótulo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ícone</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 90 }}>Desconto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {r.priceTierLabels.map(t => (
                <TableRow key={t.index}>
                  <TableCell>{t.index}</TableCell>
                  <TableCell>{t.label}</TableCell>
                  <TableCell>{t.icon || '—'}</TableCell>
                  <TableCell>{t.applyDiscount === false ? 'Não' : 'Sim'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null} />

      <SectionTitle>Cashback e Pontuação</SectionTitle>
      <BooleanField label="Gerar cashback no abastecimento" source="generateCashbackOnFillin" />
      <BooleanField label="Aceita cashback?" source="acceptsCashback" />
      <NumberField source="minimumValueToRedeemCashback" label="Mínimo para resgate de cashback" emptyText="R$ 0,00" options={{ style: 'currency', currency: 'BRL' }} />
      <NumberField source="couponRedemptionDaysLimit" label="Limite de dias para resgate de cupom" emptyText="—" />
      <BooleanField label="Pontuar quando usar desconto" source="pointsWhenDiscounting" />
    </SimpleShowLayout>
  );
};

const StationAddress = () => (
  <SimpleShowLayout sx={{ padding: 0 }}>
    <TextField source="street" label="Rua" />
    <TextField source="streetNumber" label="Número" />
    <TextField source="neighbourhood" label="Bairro" />
    <TextField source="city" label="Cidade" />
    <TextField source="state" label="Estado" />
    <TextField source="cep" label="CEP" />
    <NumberField source="latitude" label="Latitude" />
    <NumberField source="longitude" label="Longitude" />
  </SimpleShowLayout>
)

const PolygonMap = React.memo(({ coordinates }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current || !coordinates?.length) return;

    // Converter coordenadas para o formato do Google Maps
    const paths = coordinates.map(coord => ({
      lat: coord.latitude,
      lng: coord.longitude
    }));

    // Calcular centro do polígono
    const bounds = new google.maps.LatLngBounds();
    paths.forEach(path => bounds.extend(path));
    const center = bounds.getCenter();

    // Criar mapa
    const mapInstance = new google.maps.Map(mapRef.current, {
      center: { lat: center.lat(), lng: center.lng() },
      zoom: 18,
      mapId: import.meta.env.VITE_MAP_ID || 'd06eb9fab6b219cd',
    });
    mapInstanceRef.current = mapInstance;

    // Criar polígono
    const polygon = new google.maps.Polygon({
      paths,
      strokeColor: '#666666',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#999999',
      fillOpacity: 0.35,
    });
    polygon.setMap(mapInstance);
    polygonRef.current = polygon;

    // Adicionar marcadores nos vértices
    paths.forEach((path, index) => {
      const markerContent = document.createElement('div');
      markerContent.style.cssText = `
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background-color: #1976d2;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 11px;
      `;
      markerContent.textContent = String(index + 1);

      new google.maps.marker.AdvancedMarkerElement({
        position: path,
        map: mapInstance,
        content: markerContent,
        title: `Ponto ${index + 1}`,
      });
    });

    // Ajustar zoom para mostrar todo o polígono
    mapInstance.fitBounds(bounds, 50);

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }
    };
  }, [coordinates]);

  return <div ref={mapRef} style={{ height: '400px', width: '100%', borderRadius: 8 }} />;
});

const StationGps = () => {
  const record = useRecordContext();
  if (!record) { return null; }
  const coordinates = record?.coordinates || [];

  if (!coordinates.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">Nenhuma coordenada cadastrada</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Wrapper
        apiKey={import.meta.env.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
        libraries={['marker']}
        render={(status) => {
          if (status === Status.LOADING) return <Typography>Carregando mapa...</Typography>;
          if (status === Status.FAILURE) return <Typography color="error">Erro ao carregar mapa</Typography>;
          return null;
        }}
      >
        <PolygonMap coordinates={coordinates} />
      </Wrapper>

      {/* Tabela com coordenadas */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Coordenadas ({coordinates.length} pontos)</Typography>
        <ArrayField source="coordinates" label="">
          <Datagrid bulkActionButtons={false}>
            <NumberField label="Latitude" source="latitude" sortable={false} />
            <NumberField label="Longitude" source="longitude" sortable={false} />
          </Datagrid>
        </ArrayField>
      </Box>
    </Box>
  );
};

const StationSync = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <SimpleShowLayout sx={{ padding: 0 }}>
      <SelectField
        label="Sistema"
        source="syncType"
        choices={
          [
            { id: 'company-tec', name: 'CompanyTec' },
            { id: 'ez-tec', name: 'EZTec' },
            { id: 'auto-system', name: 'AutoSystem' },
          ]
        }
      />
      <DateField
        label="Último Ping"
        source="lastPingAt"
        showTime
        locales="pt-BR"
        emptyText="--"
      />
      {
        record.syncType === "auto-system" &&
        <SimpleShowLayout sx={{ padding: 0 }}>
          <TextField
            source="credentials.host"
            label="Host ou ip"
          />
          <NumberField
            source="credentials.port"
            label="Porta"
          />
          <TextField
            source="credentials.user"
            label="Usuário"
          />
          <TextField
            source="credentials.password"
            label="Senha"
            type="password"
          />
          <TextField
            source="credentials.database"
            label="Banco"
          />
          {
            record.lastInsertedTransaction && (
              <TextField
                label="Último Ponto da Sincronização"
                source="lastInsertedTransaction"
              />
            )
          }
        </SimpleShowLayout>
      }
    </SimpleShowLayout>
  )
}

const StationOpeningHours = () => (
  <ArrayField source="openingHours" label="Horários">
    <Datagrid rowClick={false} bulkActionButtons={false}>
      <FunctionField render={record => record && daysOfTheWeek.find(wd => wd.id === record.dayOfTheWeek)?.name} label="Dia" />
      <FunctionField label="Hora de abertura" render={record => {
        if (!record || record.closed || record.open24hrs || !record.startTime) {
          return "--"
        }
        return dayjs(record.startTime).format('HH:mm');
      }} />
      <FunctionField label='Hora de fechamento' render={record => {
        if (!record || record.closed || record.open24hrs || !record.endTime) {
          return "--"
        }
        return dayjs(record.endTime).format('HH:mm');
      }} />
      <FunctionField label="Aberto 24 Horas" textAlign="center"
        render={record => record && (record.open24hrs && !record.closed) ? <TrueIcon fontSize="small" /> : <FalseIcon fontSize="small" />} />
      <BooleanField source="closed" label="Fechado" textAlign="center" sortable={false} />
    </Datagrid>
  </ArrayField>
);

const StationFinancial = () => (
  <SimpleShowLayout sx={{ padding: 0 }}>
    <ReferenceField
      label="Revenda"
      source="retailerId"
      reference="retailers"
      textAlign="center"
      emptyText="Revenda não identificada"
      link={false}
      sortable={false}
    >
      <TextField source="name" />
    </ReferenceField>
    <SelectField
      source="commissioningType"
      label="Tipo de comissão"
      choices={commissioningTypeChoices}
    />
    <FunctionField label="Porcentagem da revenda"
      textAlign="center"
      render={record => {
        if (record && record.commissioningType === CommissioningType.PERCENTAGE) {
          return (
            record.commissioningValue ? `${record.commissioningValue}%` : '0%'
          );
        } else {
          return formatNumber(record?.commissioningValue || 0, { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }}
    />
    <NumberField
      source="monthlyPrice"
      label="Valor do Faturamento"
      textAlign="center"
      emptyText='R$ 0,00'
      options={{
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }}
    />
    <DateField source="billingStartDate" label="Data de início do faturamento" emptyText="--" />
    <TextField source="emailInvoice" label="E-mail para envio da nota fiscal" />
    <TextField source="emailBill" label="E-mail para envio do boleto" />
    <NumberField
      source="installationCost"
      label="Valor da instalação"
      textAlign="center"
      emptyText='R$ 0,00'
      options={{
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }}
    />
    <NumberField source="monthlyDueDate" label="Vencimento da mensalidade (dia)" />
    <NumberField source="gracePeriod" label="Período de carência" />
    <TextField source="graceReason" label="Motivo da carência" />
    <SelectField source="financePaymentMethod" label="Forma de pagamento" choices={FinancePaymentMethodType.financePaymentMethodChoices} />
    <TextField source="responsible" label="Responsável" />
    <PhoneField source="contactResponsible" label="Contato" />
  </SimpleShowLayout>
);

const StationActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      {permissions && [
        PERMISSIONS.UPDATE_STATION,
        PERMISSIONS.REGISTER_STATION_OPENING_HOURS,
        PERMISSIONS.REGISTER_STATION_ADDRESS,
        PERMISSIONS.REGISTER_STATION_FINANCIAL,
        PERMISSIONS.REGISTER_STATION_PAYMENT,
        PERMISSIONS.REGISTER_STATION_GPS,
        PERMISSIONS.REGISTER_STATION_CONCENTRATOR,
        PERMISSIONS.REGISTER_STATION_SYNCHONIZATION,
        PERMISSIONS.REGISTER_STATION_IDENTIFICATION,
      ].some(((permission) => permissions.includes(permission))) && (
          <EditButton />
        )}
    </TopToolbar>
  )
}

export default () => {
  return (
    <Show actions={<StationActions />}>
      <TabbedShowLayout>
        <Tab label="Identificação">
          <StationIdentify />
        </Tab>
        <Tab label="Configurações">
          <StationConfig />
        </Tab>
        <Tab label="Endereço">
          <StationAddress />
        </Tab>
        <Tab label="GPS">
          <StationGps />
        </Tab>
        {
          Permission({
            permission: PERMISSIONS.ADMIN,
            children: (
              <Tab label="Sincronização">
                <StationSync />
              </Tab>
            )
          })
        }
        <Tab label="Horários">
          <StationOpeningHours />
        </Tab>
        <Tab label="Feriados">
          <ArrayField source="stationHolidays" label="Feriados">
            <Datagrid bulkActionButtons={false}>
              <DateField source="date" label="Data" />
            </Datagrid>
          </ArrayField>
        </Tab>
        {
          Permission({
            permission: PERMISSIONS.REGISTER_STATION_FINANCIAL,
            children: (
              <Tab label="Financeiro">
                <StationFinancial />
              </Tab>
            )
          })
        }
        {
          Permission({
            permission: PERMISSIONS.REGISTER_STATION_GESTAO_CLICK,
            children: (
              <Tab label="Gestão Click">
                <GestaoClickStation />
              </Tab>
            )
          })
        }
        <Tab label="Mapa de Pista">
          <ListQrcodes />
          <br />
        </Tab>
        {
          Permission({
            permission: PERMISSIONS.LIST_FILLINS,
            children: (
              <Tab label="Abastecimentos" path="fillins">
                <ListFillins />
              </Tab>
            )
          })
        }
      </TabbedShowLayout>
    </Show>
  )
}
